/*
This is a component used on the page.
*/

import * as React from 'react';
import { Option, Async, AutocompleteResult } from 'react-select';
import { Options } from 'react-select';
import { PageViewModel } from '@models/common.models';

import { toast } from 'react-toastify';
import { ToastComponent, ToastType, ToastOptions } from '@common/components';

// Defining the properties the component expects
// Consider this the "setup" of the component
// All properties should be readonly

export interface TypeAheadComponentProps<T> {
  readonly renderValue?: (option: Option<T>) => JSX.Element | null | false;
  readonly getSuggestions: (value: string | undefined, id: number | undefined) => Promise<PageViewModel<Option<T>>>;
  readonly returnOption?: boolean;
  readonly errorMessage?: string;
  readonly valueKey: string;
  readonly labelKey: string;

  readonly isSearch?: boolean;
  readonly clearable?: boolean;
  readonly onSelectResetsInput?: boolean;
  readonly onBlurResetsInput?: boolean;
  readonly onCloseResetsInput?: boolean;
  readonly simpleValue?: boolean;
  readonly value?: string;
  readonly onChange: Function;
  readonly multivalued?: boolean;
  readonly placeholder?: string;
  readonly disabled?: boolean;
}

export interface TypeAheadViewModel<T> {
  value: Option<T> | '';
  priorSelections: Option<T>[];
  selectedOption: Option<T>;
  errorMessage: string;
}

// Component definition
export class TypeAheadComponent<T> extends React.Component<TypeAheadComponentProps<T>, TypeAheadViewModel<T>> {
  state: TypeAheadViewModel<T> = {
    value: '',
    priorSelections: [],
    selectedOption: {},
    errorMessage: ''
  };
  loadOptionTimer?: number;
  isDebouncing: boolean = false;

  nextOption: Option<T>;

  constructor(props: TypeAheadComponentProps<T>) {
    super(props);
  }

  async checkForValues() {
    const ids = this.state.priorSelections.map(x => x.id);

    if (!this.nextOption && this.props && this.props.value && ids.indexOf(this.props.value) === -1) {
      const daState = this.state;

      try {
        const opts = await this.props.getSuggestions(undefined, parseInt(this.props.value, 10));
        let temp = daState.priorSelections.slice();
        temp = temp.concat(opts.results);

        this.setState({ value: opts.results[0], priorSelections: temp });
      } catch (e) {
        toast.error(
          <ToastComponent type={ToastType.Error} message={'Network error, Unable to get values for type-ahead'} />,
          ToastOptions.Error
        );

        return;
      }

      return;
    }
  }

  componentDidMount() {
    return this.checkForValues();
  }

  componentWillReceiveProps() {
    return this.checkForValues();
  }

  shouldComponentUpdate(nextProps: TypeAheadComponentProps<T>, nextState: TypeAheadViewModel<T>) {
    if ((nextProps && this.props && this.props.value) || (nextState && this.state && !this.state.value)) {
      return nextProps.value !== this.state.value || nextState.value !== this.state.value;
    }

    return false;
  }

  renderError() {
    if (this.props.errorMessage) {
      return <div className="error">{this.props.errorMessage}</div>;
    }

    if (this.state.errorMessage) {
      return <div className="error">{this.state.errorMessage}</div>;
    }
    return;
  }

  renderArrow() {
    if (this.state && !this.state.value) {
      return <i className="fas fa-search" />;
    }
    return false;
  }

  renderValue(option: Option<T>) {
    return <div>{option.id}</div>;
  }

  handleMultiChange(opts: Option<T>[] | Option<T>) {
    if (!Array.isArray(opts) || opts.length === 0) {
      this.props.onChange('');
      this.setState({
        selectedOption: {},
        value: ''
      });
      return;
    }
    // send the option array to the field manager
    // include all selected options into the 'selected options'
    const temp = this.state.priorSelections.slice(); // get a snapshot of the current suggestions
    this.props.onChange(opts);
    opts.forEach((option: Option<T>) => {
      if (temp.indexOf(option) === -1) {
        temp.push(option);
      }
    });
    const lastOption = opts[opts.length - 1];
    this.setState({
      selectedOption: lastOption,
      priorSelections: temp,
      value: opts
    });
    return;
  }

  handleChange(option: Option<T>) {
    if (!option) {
      this.setState({
        selectedOption: {},
        value: ''
      });

      return;
    }

    this.nextOption = option;
    const temp = this.state.priorSelections.slice(); // get a snapshot of the current suggestions
    if (temp.indexOf(option) === -1) {
      temp.push(option);
    }

    this.setState({
      selectedOption: option,
      priorSelections: temp,
      value: option
    });
    if (this.props.returnOption) {
      this.props.onChange(option);
    } else {
      this.props.onChange(option.id);
    }
  }

  loadOpts(
    inputString: string,
    resolve: (value?: AutocompleteResult<T> | undefined) => void,
    reject: (reason?: {}) => void
  ) {
    this.isDebouncing = false;

    if (!inputString) {
      resolve({ options: [], complete: false });
      return;
    }

    this.props
      .getSuggestions(inputString, undefined)
      .then(pages => {
        const options = pages.results;
        resolve({ options: options, complete: false });
      })
      .catch(err => {
        // unable to load options
      });
  }

  render() {
    //  const { selectedOption } = this.state;
    //  const value = selectedOption && selectedOption.id;
    return (
      <div>
        {this.renderError()}
        <Async
          multi={this.props.multivalued}
          inputProps={{ onChange: this.props.onChange, value: this.props.value }}
          valueKey={this.props.valueKey}
          labelKey={this.props.labelKey}
          searchable={true}
          closeOnSelect={this.props && !this.props.multivalued}
          onBlurResetsInput={false}
          onChange={(e: Option<T> | Option<T>[]) => {
            if (this.props.multivalued) {
              this.handleMultiChange(e);
            } else {
              this.handleChange(e);
            }
          }}
          options={this.state.priorSelections}
          arrowRenderer={() => this.renderArrow()}
          optionRenderer={this.props.renderValue || this.renderValue}
          value={this.state.value}
          filterOptions={(opts: Options<T>, filter: string, currentValues: Options<T>): Options<T> => {
            return opts;
          }}
          noResultsText={'No Results Found.'}
          loadOptions={(inputString: string) => {
            const tis = this;

            const thePromise = new Promise<AutocompleteResult<T>>(
              (resolve: (value?: AutocompleteResult<T> | undefined) => void, reject: (reason?: {}) => void) => {
                if (tis.isDebouncing && tis.loadOptionTimer) {
                  clearTimeout(tis.loadOptionTimer);
                }
                tis.isDebouncing = true;
                tis.loadOptionTimer = setTimeout(tis.loadOpts.bind(tis), 300, inputString, resolve, reject);
              }
            );

            return thePromise;
          }}
          autoload={false}
          disabled={this.props.disabled}
          placeholder={this.props.placeholder || 'Select...'}
        />
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/common/components/typeahead/typeahead.component.tsx